import { formatNameLastFirst } from '@strata/helper-functions';
import { ChatWithConversationPane, type ConversationMessageStatus } from '@strata/react-chat';
import { getIn } from 'formik';

import {
    type Conversation,
    type ConversationFact,
    type ConversationMessage,
    type FactDataType,
    FactDataTypes,
    type PerformanceType,
} from 'api/userSupportAPI';
import { SocketTypes, wsSend } from 'services/socketHelpers';

export const FMH_UPN = 'ProgramSupport@freemarkethealth.com';
export const MESSAGES_LOCATION_HASH = 'messages';
export const PERF_REF_KEY_NAME = 'performance_vc_reference';

export const SocketMessageTypes = {
    STATUS: 'STATUS',
    CONVERSATIONS: 'CONVERSATIONS',
    MESSAGES: 'MESSAGES',
    USER_PARTICIPATION: 'USER_PARTI',
    CONVERSATION_PARTICIPATION: 'CONVO_PARTI',
} as const;

export interface TopicFact {
    fact_id: string;
    shape: {
        type: string;
        options_dict?: Record<string, string>;
        helper_text?: string;
        placeholder_text?: string;
        show_other?: boolean;
    };
    key_name: string;
    label: string;
    use_case: string;
    data_type: FactDataType;
    schema_id: string;
    topic_id: string;
    is_required: boolean;
    order_index: number;
    ancestor_name?: string;
    trigger_value?: string;
    is_iterable: boolean;
    add_item_label?: string;
    default_Value?: string;
    datum_value?: string | string[] | null;
}

export const formatNameMaintainCase = (
    firstName: string | undefined,
    lastName: string | undefined,
) => `${firstName ? `${firstName} ` : ''}${lastName ?? ''}`;

export interface MappedMessage {
    messageId: string;
    messageType: (typeof ChatWithConversationPane.MessageType)[keyof typeof ChatWithConversationPane.MessageType];
    senderFirstName: string;
    senderLastName: string;
    upn?: string;
    content: {
        message?: string;
        status?: ConversationMessageStatus;
        eventStatus?: string;
        completedAt?: string;
    };
    isSupportMsg: boolean;
    sentTime: string;
    conversationId: string;
}
export const mapMessage = (message: ConversationMessage): MappedMessage => ({
    content: {
        message: message.content.message,
        status: message.content.status,
        eventStatus: message.content.event_status,
        completedAt: message.content.completed_at,
    },
    conversationId: message.conversation_id,
    senderFirstName: message.first_name,
    isSupportMsg: message.is_support_msg,
    senderLastName: message.last_name,
    messageId: message.message_id,
    messageType: message.message_type,
    sentTime: message.sent_time,
    upn: message.upn,
});

export const mapDatumValue = (
    conversation: Conversation | MappedConversation,
    fact: ConversationFact,
) => {
    let mappedValue = fact?.shape?.options_dict?.[fact.datum_value ?? ''] ?? fact?.datum_value;

    if (fact?.data_type === FactDataTypes.PERFORMANCE_REF) {
        const perfDetails = conversation.details?.[FactDataTypes.PERFORMANCE_REF];
        mappedValue = perfDetails?.[fact.datum_value ?? '']?.name ?? fact.datum_value;
    }
    return mappedValue;
};

export interface MappedConversation {
    conversationId: string;
    displayId: string;
    conversationStatus: (typeof ChatWithConversationPane.ConversationStatus)[keyof typeof ChatWithConversationPane.ConversationStatus];
    createdByUpn: string;
    createdByFirstName?: string;
    createdByLastName?: string;
    createdTime: string;
    closedByUpn?: string;
    closedByFirstName?: string;
    closedByLastName?: string;
    closedTime?: string;
    details: Conversation['details'];
    isNote: boolean;
    referralId?: string;
    referralFriendlyId?: string;
    memberFullName?: string;
    supplierNpi: string;
    topicId?: string;
    messages?: MappedMessage[];
    lastMessage?: string;
    isUnread?: boolean;
    lastUpdated?: string;
    createdBySupportUser: boolean;
    subject: { title: string; isUnread?: boolean };
    conversationType: string;
    createdBy: string;
}
export const mapConversation = (conversation: Conversation): MappedConversation => {
    const message = conversation.last_message ? mapMessage(conversation.last_message) : undefined;
    const firstFact = conversation.first_fact;
    const datumValue = mapDatumValue(conversation, firstFact);
    const truncDatumValue = datumValue?.substring(0, 200);
    const truncMessage = message?.content?.message?.substring(0, 200);
    const subject = `${conversation.short_title} | ${truncDatumValue}`;

    return {
        conversationId: conversation.conversation_id,
        displayId: conversation.display_id,
        conversationStatus: conversation.conversation_status,
        createdByUpn: conversation.created_by_upn,
        createdByFirstName: conversation.created_by_first_name,
        createdByLastName: conversation.created_by_last_name,
        createdTime: conversation.created_time,
        closedByUpn: conversation.closed_by_upn,
        closedByFirstName: conversation.closed_by_first_name,
        closedByLastName: conversation.closed_by_last_name,
        closedTime: conversation.closed_time,
        details: {
            ...conversation.details,
            subject,
        },
        isNote: conversation?.is_note,
        referralId: conversation.referral_id,
        referralFriendlyId: conversation.referral_friendly_id,
        memberFullName: formatNameLastFirst(
            conversation?.member_last_name,
            conversation?.member_first_name,
        ),
        supplierNpi: conversation?.supplier_npi,
        topicId: conversation?.topic_id,
        messages: conversation?.messages?.map(mapMessage),
        lastMessage: message && `${message.senderFirstName}: ${truncMessage}`,
        isUnread: conversation?.is_unread,
        lastUpdated: conversation?.last_updated,
        createdBySupportUser: conversation.created_by_support_user,
        subject: {
            title: subject,
            isUnread: conversation.is_unread,
        },
        conversationType: conversation.referral_id ? 'Referral' : 'General',
        createdBy: formatNameMaintainCase(
            conversation.created_by_first_name,
            conversation.created_by_last_name,
        ),
    };
};

export const isFactValid = (
    fact: TopicFact,
    facts: Record<string, TopicFact>,
    details?: {
        [FactDataTypes.PERFORMANCE_REF]?: Record<string, { name: string; type: PerformanceType }>;
    },
) => {
    if (fact?.ancestor_name != null) {
        if (fact.ancestor_name.includes(`${PERF_REF_KEY_NAME}.`)) {
            const perfDetailPath = fact.ancestor_name.slice(PERF_REF_KEY_NAME.length + 1);
            const perfKey = getIn(facts, `${PERF_REF_KEY_NAME}.datum_value`) as string;
            const perfDetails = details?.[FactDataTypes.PERFORMANCE_REF];
            if (getIn(perfDetails?.[perfKey], perfDetailPath) === fact.trigger_value) {
                return true;
            }
        } else {
            const ancestorFactKey = Object.keys(facts).find(
                (key) =>
                    facts[key].key_name === fact.ancestor_name && facts[key].datum_value != null,
            );
            if (
                ancestorFactKey &&
                getIn(facts, `${ancestorFactKey}.datum_value`) === fact.trigger_value
            ) {
                return true;
            }
        }
        return false;
    }
    return true;
};

export const detailFactsSort = (a: ConversationFact, b: ConversationFact) => {
    if (a?.data_type === FactDataTypes.MESSAGE_TEMPLATE) {
        return 1;
    }
    if (b?.data_type === FactDataTypes.MESSAGE_TEMPLATE) {
        return -1;
    }
    return (a?.datum_index ?? 0) - (b?.datum_index ?? 0);
};

// WS calls for User Support to be called with dispatch
export const setUserSupportReferral = (referralId: string | null | undefined) =>
    wsSend({
        socketType: SocketTypes.USER_SUPPORT,
        messageType: 'SET-REFERRAL',
        message: {
            referral_id: referralId,
        },
    });

export const setUserSupportConversation = (conversationId: string | null | undefined) =>
    wsSend({
        socketType: SocketTypes.USER_SUPPORT,
        messageType: 'SET-CONVERSATION',
        message: {
            conversation_id: conversationId,
        },
    });

export const sendUserSupportMessage = (message: {
    conversation_id: string;
    details: { content: { message: string } };
}) =>
    wsSend({
        socketType: SocketTypes.USER_SUPPORT,
        messageType: 'SEND-MESSAGE',
        message,
    });

export interface ConversationParticipation {
    conversation_id: string;
    conversation_status: (typeof ChatWithConversationPane.ConversationStatus)[keyof typeof ChatWithConversationPane.ConversationStatus];
    supplier_npi: string;
    referral_id: string;
    last_message_id?: string;
}
export type ReferralConversationCounts = Record<
    string,
    { open: number; unread: number; conversations: Record<string, boolean> }
>;
/**
 * When conversations would be unread:
 * 1. no participant row, or
 * 3. conversation has last_message_id & participant row doesn't match
 *
 * ReferralConversationCounts looks like:
 * [referralId]: {
 *      open: number,
 *      unread: number,
 *      conversations: {
 *          [conversationId]: boolean, # is_unread: true if unread, false if read
 *     },
 * }
 */
export const getUpdatedReferralConversationCounts = (
    userParticipation?: Record<string, string | undefined>,
    conversationParticipation?: Record<string, ConversationParticipation>,
) => {
    const newCounts: ReferralConversationCounts = {};
    Object.values(conversationParticipation ?? {}).forEach((conversation) => {
        const {
            referral_id: referralId,
            conversation_status: conversationStatus,
            conversation_id: conversationId,
            last_message_id: lastMessageId,
        } = conversation;
        const isOpen = conversationStatus === ChatWithConversationPane.ConversationStatus.OPEN;
        if (!newCounts[referralId]) {
            // Initialize if we haven't seen this referral yet
            newCounts[referralId] = {
                open: 0,
                unread: 0,
                conversations: {},
            };
        }
        if (isOpen) {
            newCounts[referralId].open += 1;
        }

        // Evaluate whether the conversation is unread
        if (
            !(conversationId in (userParticipation ?? {})) ||
            (lastMessageId !== null && lastMessageId !== userParticipation?.[conversationId])
        ) {
            // Only count unread if the conversation is open
            if (isOpen) {
                newCounts[referralId].unread += 1;
            }
            newCounts[referralId].conversations[conversationId] = true;
        } else {
            newCounts[referralId].conversations[conversationId] = false;
        }
    });
    return newCounts;
};

export const ConversationContexts = {
    CLAIM_REJECTION: 'claim_rejection',
    NEEDS_BY_DATE: 'needs_by_date',
    PATIENT_DISCONTINUED_THERAPY: 'patient_discont',
} as const;
export type ConversationContext = (typeof ConversationContexts)[keyof typeof ConversationContexts];
